import { Canvas, useFrame } from "@react-three/fiber";
import { useMemo, useRef, useState } from "react";
import * as THREE from "three";

// Configuration
const NUM_NODES = 15;
const NODE_SIZE = 0.3;
const CONNECTION_DISTANCE = 10;
const BOUNDS = 2.5;
const LINE_WIDTH = 0.024;
const MAX_CONNECTIONS_PER_NODE = 5;
const NODE_COLORS = ["#0f9d8a", "#4db9ff", "#ff6b6b", "#ffc857", "#7bd88f"];

interface NodeData {
	id: string;
	position: THREE.Vector3;
	velocity: THREE.Vector3;
	color: string;
}

interface NodeProps {
	nodeData: NodeData;
}

function Node({ nodeData }: NodeProps) {
	const meshRef = useRef<THREE.Mesh>(null);
	const pulseOffset = useMemo(() => Math.random() * Math.PI * 2, []);

	useFrame(() => {
		if (!meshRef.current) return;

		// Update position with velocity
		const newX = nodeData.position.x + nodeData.velocity.x;
		const newY = nodeData.position.y + nodeData.velocity.y;
		const newZ = nodeData.position.z + nodeData.velocity.z;

		// Bounce off boundaries and clamp position
		if (newX > BOUNDS || newX < -BOUNDS) {
			nodeData.velocity.x *= -1;
			nodeData.position.x = Math.max(
				-BOUNDS,
				Math.min(BOUNDS, nodeData.position.x),
			);
		} else {
			nodeData.position.x = newX;
		}

		if (newY > BOUNDS || newY < -BOUNDS) {
			nodeData.velocity.y *= -1;
			nodeData.position.y = Math.max(
				-BOUNDS,
				Math.min(BOUNDS, nodeData.position.y),
			);
		} else {
			nodeData.position.y = newY;
		}

		if (newZ > BOUNDS || newZ < -BOUNDS) {
			nodeData.velocity.z *= -1;
			nodeData.position.z = Math.max(
				-BOUNDS,
				Math.min(BOUNDS, nodeData.position.z),
			);
		} else {
			nodeData.position.z = newZ;
		}

		meshRef.current.position.copy(nodeData.position);
		meshRef.current.scale.setScalar(
			1 + Math.sin(performance.now() / 600 + pulseOffset) * 0.08,
		);
	});

	return (
		<mesh ref={meshRef} position={nodeData.position}>
			<sphereGeometry args={[NODE_SIZE, 16, 16]} />
			<meshStandardMaterial
				color={nodeData.color}
				emissive={nodeData.color}
				emissiveIntensity={0.35}
				metalness={0.6}
				roughness={0.3}
			/>
		</mesh>
	);
}

interface ConnectionsProps {
	nodes: NodeData[];
}

interface Connection {
	start: THREE.Vector3;
	end: THREE.Vector3;
}

function ThickLine({
	start,
	end,
}: {
	start: THREE.Vector3;
	end: THREE.Vector3;
}) {
	const meshRef = useRef<THREE.Mesh>(null);

	useFrame(() => {
		if (!meshRef.current) return;

		// Calculate position (midpoint between start and end)
		const midpoint = new THREE.Vector3()
			.addVectors(start, end)
			.multiplyScalar(0.5);
		meshRef.current.position.copy(midpoint);

		// Calculate direction and rotation
		const direction = new THREE.Vector3().subVectors(end, start);
		const length = direction.length();

		// Orient cylinder to point from start to end
		meshRef.current.quaternion.setFromUnitVectors(
			new THREE.Vector3(0, 1, 0),
			direction.clone().normalize(),
		);

		// Set scale (height = length of connection)
		meshRef.current.scale.set(1, length, 1);
	});

	return (
		<mesh ref={meshRef}>
			<cylinderGeometry args={[LINE_WIDTH, LINE_WIDTH, 1, 8]} />
			<meshBasicMaterial color="#9fe5d7" transparent opacity={0.6} />
		</mesh>
	);
}

function Connections({ nodes }: ConnectionsProps) {
	const [connections, setConnections] = useState<Connection[]>([]);

	useFrame(() => {
		const newConnections: Connection[] = [];
		const connectionCounts = new Map<number, number>();

		// Initialize connection counts
		for (let i = 0; i < nodes.length; i++) {
			connectionCounts.set(i, 0);
		}

		for (let i = 0; i < nodes.length; i++) {
			for (let j = i + 1; j < nodes.length; j++) {
				const distance = nodes[i].position.distanceTo(nodes[j].position);

				if (distance < CONNECTION_DISTANCE) {
					// Check if either node has reached max connections
					const iConnections = connectionCounts.get(i) || 0;
					const jConnections = connectionCounts.get(j) || 0;

					if (
						iConnections < MAX_CONNECTIONS_PER_NODE &&
						jConnections < MAX_CONNECTIONS_PER_NODE
					) {
						newConnections.push({
							start: nodes[i].position,
							end: nodes[j].position,
						});

						// Increment connection counts for both nodes
						connectionCounts.set(i, iConnections + 1);
						connectionCounts.set(j, jConnections + 1);
					}
				}
			}
		}

		setConnections(newConnections);
	});

	return (
		<>
			{connections.map((conn, index) => (
				<ThickLine
					key={`${conn.start.x}-${conn.start.y}-${conn.end.x}-${conn.end.y}-${index}`}
					start={conn.start}
					end={conn.end}
				/>
			))}
		</>
	);
}

function Scene() {
	// Initialize nodes with random positions and velocities
	const nodes = useMemo<NodeData[]>(() => {
		const nodeArray: NodeData[] = [];
		for (let i = 0; i < NUM_NODES; i++) {
			const position = new THREE.Vector3(
				(Math.random() - 0.5) * BOUNDS,
				(Math.random() - 0.5) * BOUNDS,
				(Math.random() - 0.5) * BOUNDS,
			);
			const velocity = new THREE.Vector3(
				(Math.random() - 0.5) * 0.01,
				(Math.random() - 0.5) * 0.01,
				(Math.random() - 0.5) * 0.01,
			);
			nodeArray.push({
				id: `node-${i}`,
				position,
				velocity,
				color: NODE_COLORS[Math.floor(Math.random() * NODE_COLORS.length)],
			});
		}
		return nodeArray;
	}, []);

	return (
		<>
			{/* Lighting */}
			<ambientLight intensity={0.5} />
			<pointLight position={[-5, -5, 5]} intensity={35} color="#0f9d8a" />
			<pointLight position={[5, 5, 5]} intensity={45} color="#ff6b6b" />
			<pointLight position={[0, 5, -5]} intensity={25} color="#4db9ff" />

			{/* Nodes */}
			{nodes.map((nodeData) => (
				<Node key={nodeData.id} nodeData={nodeData} />
			))}

			{/* Connections */}
			<Connections nodes={nodes} />
		</>
	);
}

export function InteractiveGraph() {
	return (
		<div className="w-full h-full">
			<Canvas
				camera={{ position: [0, 0, 10], fov: 50 }}
				style={{
					background:
						"radial-gradient(circle at top, rgba(15,157,138,0.08), transparent 60%), #f3fbf7",
				}}
			>
				<fog attach="fog" args={["#f3fbf7", 8, 16]} />
				<Scene />
			</Canvas>
		</div>
	);
}
